/**
 * Bloody Mary — „Battle of Legends, Volume Two".
 *
 * Na začiatku ťahu s presne 3 kartami v ruke získa akciu navyše. Viac kariet
 * sa preto pýta „je toto tvoja tretia akcia?" — `turn.actionsUsed` má v tej
 * chvíli presne 3 položky (útok sa zapíše už pri vyhlásení).
 */

import { neighbors } from '../map.js';
import type { Ctx } from '../ops.js';
import { askChoice, cardDef, dealDamage, discardFromHand, drawCard, gainAction, getMap, getPlayer, healFighter, heroOf } from '../ops.js';
import type { CardId, PlayerIndex } from '../types.js';
import { draw, feint } from './common.js';
import { leftStartZone } from './goldenBat.js';
import { askBoost, askMove, fighterOrNull, opponentOf, revealHand } from './helpers.js';
import { ambush } from './robinHood.js';
import { closerThanSheAppears } from './tRex.js';
import type { CardEffect, EffectCtx, EffectRegistry, HeroSpecial } from './types.js';

const MARY = 'bloody-mary';
const GLASS = 'bloody-mary.broken-glass';
const STOLEN = 'bloody-mary.stolen-memories';

/** „If this is your third action this turn" — len na vlastnom ťahu. */
const thirdAction = (e: EffectCtx) =>
  e.ctx.state.turn.player === e.player && e.ctx.state.turn.actionsUsed.length === 3;

const maryOf = (ctx: Ctx, player: PlayerIndex) =>
  ctx.state.fighters.find((f) => f.owner === player && f.defId === MARY) ?? null;

/** „DURING: Your opponent discards 1 random card. Add its BOOST value… AFTER: If this is your third action, draw 1 card." */
const outOfTheMirror: CardEffect = {
  duringChoice: ambush.duringChoice,
  after: (e) => {
    if (thirdAction(e)) drawCard(e.ctx, e.player);
  },
};

/** „DURING: You may BOOST this attack. AFTER: If this is your third action this turn, recover 3 health." */
const ghostlyTouch: CardEffect = {
  duringChoice: (e) => askBoost(e, 'Ghostly Touch: smieš boostovať útok'),
  after: (e) => {
    const mary = maryOf(e.ctx, e.player);
    if (mary && thirdAction(e)) healFighter(e.ctx, mary.id, 3);
  },
};

/** „If this is your third action this turn, this card's value is 7 instead." */
const speakThreeTimes: CardEffect = {
  during: (e, value) => (thirdAction(e) ? 7 : value),
};

/**
 * „BLOODY REPRISE: 0 ATK. DURING COMBAT: If your opponent played a card
 * against BLOODY REQUIEM, this attack's value is that card's printed value."
 * Bonusový útok ako Relentless Assault — hodnota sa určí už teraz.
 */
const bloodyRequiem: CardEffect = {
  after: (e) => {
    if (!e.fighterId || !e.opponentFighterId) return;
    const defense = e.ctx.state.combat?.defenseCard;
    const value = defense ? cardDef(e.ctx, defense).value : 0;
    askChoice(e.ctx, {
      kind: 'bonusAttack',
      player: e.player,
      source: e.cardId,
      label: `Bloody Reprise: bonusový útok za ${value}`,
      optional: false,
      attackerFighter: e.fighterId,
      defenderFighter: e.opponentFighterId,
      name: 'Bloody Reprise',
      value,
    });
  },
};

/** „You may place Bloody Mary in any empty space adjacent to the opposing fighter." */
const trickOfTheLight: CardEffect = {
  after: (e) => {
    const mary = maryOf(e.ctx, e.player);
    const them = fighterOrNull(e.ctx, e.opponentFighterId);
    if (!mary || !them) return;
    const occupied = new Set(e.ctx.state.fighters.map((f) => f.spaceId));
    const spaces = neighbors(getMap(e.ctx), them.spaceId).filter((id) => !occupied.has(id));
    if (spaces.length === 0) return;
    askChoice(e.ctx, {
      kind: 'place',
      player: e.player,
      source: e.cardId,
      label: 'Trick of the Light: smieš položiť Bloody Mary vedľa súperovej figúrky',
      optional: true,
      fighterId: mary.id,
      spaces,
    });
  },
};

const side = (e: EffectCtx) => (e.role === 'defender' ? 'defender' : 'attacker');

/**
 * „DURING: You may increase or decrease the value of this card by 1. AFTER:
 * If the value of this card matches your opponent's card, draw 1 card and the
 * opposing fighter takes 2 damage."
 */
const brokenGlass: CardEffect = {
  duringChoice: (e) => {
    const s = side(e);
    askChoice(e.ctx, {
      kind: 'option',
      player: e.player,
      source: e.cardId,
      label: 'Broken Glass: smieš zmeniť hodnotu karty o 1',
      optional: true,
      options: [
        { label: '+1', follow: { kind: 'effect', effect: GLASS, data: `${s}:1` } },
        { label: '−1', follow: { kind: 'effect', effect: GLASS, data: `${s}:-1` } },
      ],
    });
  },
  onFollow: (ctx, _choice, data) => {
    const combat = ctx.state.combat;
    if (combat) (combat.flags ??= []).push(`glass:${data}`);
  },
  during: (e, value) => {
    const flag = e.ctx.state.combat?.flags?.find((f) => f.startsWith(`glass:${side(e)}:`));
    return flag ? value + Number(flag.split(':')[2]) : value;
  },
  after: (e) => {
    const combat = e.ctx.state.combat;
    if (!combat?.values) return;
    // Súper musí nejakú kartu zahrať — bez nej nie je s čím porovnávať.
    const theirs = e.role === 'defender' ? combat.attackCard : combat.defenseCard;
    if (!theirs) return;
    const mine = e.role === 'defender' ? combat.values.defense : combat.values.attack;
    const other = e.role === 'defender' ? combat.values.attack : combat.values.defense;
    if (mine !== other) return;
    drawCard(e.ctx, e.player);
    if (e.opponentFighterId) dealDamage(e.ctx, e.opponentFighterId, 2);
  },
};

/**
 * „Look at an opponent's hand and choose a card. Your opponent may discard it.
 * If they don't, their hero takes damage equal to its BOOST value."
 */
const stolenMemories: CardEffect = {
  scheme: (e) => {
    const opponent = opponentOf(e);
    if (opponent === null || getPlayer(e.ctx.state, opponent).hand.length === 0) return;
    if (!revealHand(e, opponent)) return;
    askChoice(e.ctx, {
      kind: 'card',
      player: e.player,
      source: e.cardId,
      label: 'Stolen Memories: vyber kartu zo súperovej ruky',
      optional: false,
      from: 'hand',
      owner: opponent,
      purpose: 'effect',
    });
  },
  onCardChosen: (ctx, choice, cardId) => {
    const owner = choice.owner ?? choice.player;
    const def = cardDef(ctx, cardId);
    askChoice(ctx, {
      kind: 'option',
      player: owner,
      source: choice.source,
      label: `Stolen Memories: odhodíš ${def.name}? Inak dostane hrdina ${def.boost} poškodenia.`,
      optional: false,
      options: [
        { label: `Odhodiť ${def.name}`, follow: { kind: 'effect', effect: STOLEN, data: `discard:${cardId}` } },
        { label: `Hrdina dostane ${def.boost}`, follow: { kind: 'effect', effect: STOLEN, data: `damage:${cardId}` } },
      ],
    });
  },
  onFollow: (ctx, choice, data) => {
    const [what, cardId] = data.split(':') as [string, CardId];
    const player = choice.player;
    if (what === 'discard') {
      if (getPlayer(ctx.state, player).hand.includes(cardId)) discardFromHand(ctx, player, cardId, 'effect', true);
      return;
    }
    const hero = heroOf(ctx.state, player);
    if (hero) dealDamage(ctx, hero.id, cardDef(ctx, cardId).boost);
  },
};

/** „Choose one of the fighters in the combat and move them up to 4 spaces." */
const infinityMirror: CardEffect = {
  after: (e) => {
    const inCombat = [e.fighterId, e.opponentFighterId].filter((id): id is string => Boolean(id));
    askMove(e, { fighters: inCombat, max: 4, label: 'Infinity Mirror: presuň jednu figúrku zo súboja až o 4' });
  },
};

/** „The value of this card is equal to the printed value of your opponent's card." */
const mirrorImage: CardEffect = {
  during: (e) => {
    const combat = e.ctx.state.combat;
    if (!combat) return 0;
    if (combat.bonus) return combat.bonus.value;
    return combat.attackCard ? cardDef(e.ctx, combat.attackCard).value : 0;
  },
};

/** „If Bloody Mary shares no zones with the space she started in this turn, this card's value is 6 instead." */
const jumpScare: CardEffect = {
  during: (e, value) => (leftStartZone(e) ? 6 : value),
};

export const bloodyMaryCards: EffectRegistry = {
  'bloody-mary.out-of-the-mirror': outOfTheMirror,
  'bloody-mary.ghostly-touch': ghostlyTouch,
  'bloody-mary.speak-three-times': speakThreeTimes,
  'bloody-mary.bloody-requiem': bloodyRequiem,
  'bloody-mary.feint': feint,
  'bloody-mary.trick-of-the-light': trickOfTheLight,
  [GLASS]: brokenGlass,
  'bloody-mary.jump-scare': jumpScare,
  [STOLEN]: stolenMemories,
  'bloody-mary.infinity-mirror': infinityMirror,
  'bloody-mary.mirror-image': mirrorImage,
  'bloody-mary.evade': draw(1),
  'bloody-mary.closer-than-she-appears': closerThanSheAppears,
};

export const bloodyMarySpecial: HeroSpecial = {
  name: 'Bloody Mary',
  /** „At the start of your turn, if you have exactly 3 cards in hand, gain 1 action." */
  startOfTurn: (ctx, player) => {
    if (getPlayer(ctx.state, player).hand.length === 3) gainAction(ctx, player);
  },
};
